///
/// EarthBrowser
///
/// Created by Matt Giger on 1/11/2025
/// Copyright (c) 2025 Geodata Labs
///

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogOverlay, DialogPortal, DialogTitle } from "@/components/ui/dialog"
import { useAddBetaTesterMutation } from "@/redux/betaTesterApi"
import { setBetaTestOpen } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { zodResolver } from "@hookform/resolvers/zod"
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"
import { useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { toast } from "@/components/ui/use-toast"
import { isProduction, isTauriApp } from "@/utils/environment"
import { Sparkles, Rocket, Mail } from "lucide-react"

const betatestFormSchema = z.object({
	email: z.string().min(6).max(320).email(),
})

export function BetaTestDialog() {
	const dispatch = useAppDispatch()
	const [isComplete, setIsComplete] = useState(false)
	const [, setTurnstileStatus] = useState<"success" | "error" | "expired" | "required">("required")
	const turnstileWidgetRef = useRef<TurnstileInstance | null>(null)
	const betaTestOpen = useAppSelector((state) => state.dialog.betaTestOpen)
	const [addBetaTester] = useAddBetaTesterMutation()

	const form = useForm<z.infer<typeof betatestFormSchema>>({
		resolver: zodResolver(betatestFormSchema),
		defaultValues: {
			email: "",
		},
	})

	async function onSubmit(values: z.infer<typeof betatestFormSchema>) {
		const freshToken = turnstileWidgetRef.current?.getResponse() ?? ""
		if(isProduction() && !isTauriApp() && !freshToken) {
			toast({
				title: "Captcha Error",
				variant: "destructive",
				description: "Captcha error, please try again",
			})
			return
		}

		try {
			const result = await addBetaTester({ email: values.email, turnstileToken: freshToken }).unwrap()
			if(result.success) {
				toast({
					title: "Sign Up Success",
					variant: "default",
					description: "Thank you for signing up",
				})
				setIsComplete(true)
				dispatch(setBetaTestOpen(false))
			} else {
				toast({
					title: "Sign Up Error",
					variant: "destructive",
					description: result.message || "Please try again",
				})
			}
		} catch (error: unknown) {
			const fetchError = error as { data?: { message?: string }, status?: number }
			const message = fetchError?.data?.message || "Something went wrong. Please try again."
			toast({
				title: "Sign Up Error",
				variant: "destructive",
				description: message,
			})
		}
	}

	return (
		<Dialog open={betaTestOpen} onOpenChange={(open) => dispatch(setBetaTestOpen(open))}>
		<DialogPortal>
			<DialogOverlay className="fixed inset-0 bg-black/40 backdrop-blur-sm" />

			<DialogContent className="overflow-hidden p-0 max-w-md rounded-3xl" aria-describedby="">
				<div className="relative bg-background/95 backdrop-blur-xl border border-border/50 shadow-2xl p-6">

					{/* Header with Icon */}
					<DialogHeader className="space-y-4 mb-6">
						<div className="flex items-center justify-center mb-4">
							{/* Icon with gradient glow */}
							<div className="relative">
								<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-500 rounded-2xl blur-lg opacity-75" />
								<div className="relative bg-blue-500/20 p-3 rounded-2xl border border-blue-500/30">
									<Rocket className="h-8 w-8 text-cyan-500" />
								</div>
							</div>
						</div>
					<DialogTitle className="text-2xl font-bold text-center bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 bg-clip-text text-transparent">
						Join the Expedition
					</DialogTitle>
						<DialogDescription className="text-center text-muted-foreground">
							Sign up for early access and be among the first explorers
						</DialogDescription>
					</DialogHeader>

					{/* Form */}
					<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">

						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
								<FormLabel className="text-sm font-semibold text-foreground flex items-center gap-2">
									<Mail className="h-4 w-4 text-cyan-600" />
									Email Address
								</FormLabel>
								<FormControl>
									<Input
										placeholder="explorer@example.com"
										autoComplete="email"
										className={`rounded-xl border-2 transition-all duration-300 ${
											isComplete
												? "border-transparent bg-muted/50"
												: "border-cyan-500/30 focus:border-cyan-600 bg-background shadow-sm"
										}`}
										disabled={isComplete}
										required
										{...field} />
								</FormControl>
								</FormItem>
							)}/>

						<Button
							type="submit"
							className="w-full rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white disabled:hover:scale-100 disabled:opacity-50"
							disabled={isComplete || !form.formState.isValid}>
								{isComplete ? (
									<>
										<Sparkles className="h-4 w-4 mr-2" />
										<span className="font-medium">Done</span>
									</>
								) : (
									<>
										<Rocket className="h-4 w-4 mr-2" />
										<span className="font-medium">Request Access</span>
									</>
								)}
							</Button>

					{(isProduction() && !isTauriApp()) && (
						<div className="flex justify-center pt-4">
							<Turnstile
								ref={turnstileWidgetRef}
								siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY!}
								onError={() => { setTurnstileStatus("error") }}
								onExpire={() => { setTurnstileStatus("expired") }}
								options={{
									action: "submit-form",
									theme: "auto",
									size: "flexible",
									refreshExpired: "auto",
									retry: "auto",
								}}
								onSuccess={() => {
									setTurnstileStatus("success")
								}}
							/>
						</div>
					)}
					</form>
					</Form>

				</div>
			</DialogContent>
		</DialogPortal>
		</Dialog>
	)
}
